import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Layers, Plus, Trash2 } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface LessonForm {
  key: number
  title: string
  contentType: 'video' | 'article' | 'document' | 'link'
  contentUrl: string
  body: string
  durationMinutes: string
}

interface ModuleForm {
  key: number
  title: string
  lessons: LessonForm[]
}

interface ManageLesson {
  id: string
  title: string
  contentType: LessonForm['contentType']
  contentUrl: string | null
  body: string | null
  durationMinutes: number
}

interface ManageModule {
  id: string
  title: string
  lessons: ManageLesson[]
}

let keySeq = 1
const newLesson = (): LessonForm => ({
  key: keySeq++,
  title: '',
  contentType: 'article',
  contentUrl: '',
  body: '',
  durationMinutes: '',
})
const newModule = (): ModuleForm => ({ key: keySeq++, title: '', lessons: [newLesson()] })

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function ContentForm() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')

  const [modules, setModules] = useState<ModuleForm[]>([newModule()])
  const [exists, setExists] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const tree = await apiJson<ManageModule[]>(`/courses/${courseId}/content/manage`)
      if (tree.length > 0) {
        setModules(
          tree.map((m) => ({
            key: keySeq++,
            title: m.title,
            lessons: m.lessons.map((l) => ({
              key: keySeq++,
              title: l.title,
              contentType: l.contentType,
              contentUrl: l.contentUrl ?? '',
              body: l.body ?? '',
              durationMinutes: l.durationMinutes ? String(l.durationMinutes) : '',
            })),
          })),
        )
        setExists(true)
      }
    } catch {
      // no content yet -> keep the blank editor
    } finally {
      setLoading(false)
    }
  }, [courseId])

  useEffect(() => {
    load()
  }, [load])

  const patchModule = (key: number, patch: Partial<ModuleForm>) =>
    setModules((ms) => ms.map((m) => (m.key === key ? { ...m, ...patch } : m)))

  const patchLesson = (mk: number, lk: number, patch: Partial<LessonForm>) =>
    setModules((ms) =>
      ms.map((m) =>
        m.key === mk
          ? { ...m, lessons: m.lessons.map((l) => (l.key === lk ? { ...l, ...patch } : l)) }
          : m,
      ),
    )

  const valid =
    modules.length > 0 &&
    modules.every(
      (m) =>
        m.title.trim().length > 0 &&
        m.lessons.length > 0 &&
        m.lessons.every(
          (l) =>
            l.title.trim().length > 0 &&
            (l.contentType === 'article' ? l.body.trim().length > 0 : l.contentUrl.trim().length > 0),
        ),
    )

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = {
        modules: modules.map((m) => ({
          title: m.title.trim(),
          lessons: m.lessons.map((l) => ({
            title: l.title.trim(),
            contentType: l.contentType,
            ...(l.contentType === 'article'
              ? { body: l.body }
              : { contentUrl: l.contentUrl.trim() }),
            durationMinutes: l.durationMinutes ? parseInt(l.durationMinutes, 10) : 0,
          })),
        })),
      }
      await apiJson(`/courses/${courseId}/content`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      })
      navigate(`/courses/${courseId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can manage course content.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to course
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Layers size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{exists ? 'Edit Content' : 'Add Content'}</h1>
            <p className="text-xs text-slate-400">
              Modules and lessons learners work through — lesson completion drives course progress
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-5 px-6 py-6"
        >
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700">
            Saving replaces the whole content tree and clears existing lesson completions for this course.
          </div>

          {modules.map((m, mi) => (
            <div key={m.key} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Module {mi + 1}</p>
                {modules.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setModules((ms) => ms.filter((x) => x.key !== m.key))}
                    className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Remove module"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <input
                maxLength={200}
                value={m.title}
                onChange={(e) => patchModule(m.key, { title: e.target.value })}
                placeholder="Module title…"
                className={`${inputCls} mt-2 bg-white font-semibold`}
              />

              <div className="mt-4 space-y-3">
                {m.lessons.map((l, li) => (
                  <div key={l.key} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Lesson {li + 1}</p>
                      {m.lessons.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            patchModule(m.key, { lessons: m.lessons.filter((x) => x.key !== l.key) })
                          }
                          className="rounded-lg p-1 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Remove lesson"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Title *</label>
                        <input
                          maxLength={300}
                          value={l.title}
                          onChange={(e) => patchLesson(m.key, l.key, { title: e.target.value })}
                          placeholder="Lesson title…"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Type</label>
                        <select
                          value={l.contentType}
                          onChange={(e) =>
                            patchLesson(m.key, l.key, { contentType: e.target.value as LessonForm['contentType'] })
                          }
                          className={inputCls}
                        >
                          <option value="article">Article</option>
                          <option value="video">Video</option>
                          <option value="document">Document</option>
                          <option value="link">Link</option>
                        </select>
                      </div>
                    </div>
                    {l.contentType === 'article' ? (
                      <div className="mt-3">
                        <label className={labelCls}>Body *</label>
                        <textarea
                          rows={4}
                          maxLength={50000}
                          value={l.body}
                          onChange={(e) => patchLesson(m.key, l.key, { body: e.target.value })}
                          placeholder="Lesson text…"
                          className={`${inputCls} resize-y`}
                        />
                      </div>
                    ) : (
                      <div className="mt-3">
                        <label className={labelCls}>URL *</label>
                        <input
                          maxLength={2000}
                          value={l.contentUrl}
                          onChange={(e) => patchLesson(m.key, l.key, { contentUrl: e.target.value })}
                          placeholder={l.contentType === 'video' ? 'https://… (video URL)' : 'https://…'}
                          className={inputCls}
                        />
                      </div>
                    )}
                    <div className="mt-3 w-32">
                      <label className={labelCls}>Minutes</label>
                      <input
                        type="number"
                        min={0}
                        max={6000}
                        value={l.durationMinutes}
                        onChange={(e) => patchLesson(m.key, l.key, { durationMinutes: e.target.value })}
                        placeholder="0"
                        className={inputCls}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => patchModule(m.key, { lessons: [...m.lessons, newLesson()] })}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-2.5 text-xs font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-600"
              >
                <Plus size={13} /> Add lesson
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => setModules((ms) => [...ms, newModule()])}
            disabled={modules.length >= 30}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-600"
          >
            <Plus size={15} /> Add module
          </button>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to={`/courses/${courseId}`}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !valid}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : exists ? 'Save content' : 'Create content'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
